import CopyAllIcon from '@mui/icons-material/CopyAll';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { transactionsActions, type transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { type CashFlowTransaction, TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import { InvoiceFormUpsertAction } from 'containers/InvoiceForm/actions';
import InvoiceStatusChip from 'containers/Invoices/InvoiceStatusChip';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import type { TxnOptionProps } from './types';
import { usePaymentOptions } from './usePaymentOptions';

type ResourceTxnAction = { clientId?: string; id: string; resource: CashFlowTransaction };

export const useInvoiceOptionsRaw = (
  props: TxnOptionProps & {
    /** Used if we only want to show payment related options. */
    paymentOnly?: boolean;
  },
) => {
  const { closeMenu, txnActionProps, paymentOnly } = props;
  const { txnToEdit: txn } = txnActionProps;
  const dispatch = useAppDispatch();
  const { saveTransactions } = useTxnActions({
    callback: (ret: ResourceTxnAction[]) => {
      if (ret && ret.length > 0) {
        const invoice = ret.find((t) => t.resource.type === 'INVOICE')?.resource;
        dispatch(InvoiceFormUpsertAction({ context: '3-dot-menu', invoice }));
      }
    },
  });

  const updateEstimateStatus = useCallback(
    (txn: transactionsTypes.TransactionRecord, status: transactionsTypes.EstimateStatus) => {
      closeMenu();
      const updatedTxn = txn.set('estimateStatus', status);
      dispatch(transactionsActions.updateTransaction(updatedTxn));
    },
    [dispatch, closeMenu],
  );

  const nextInvoiceNo = useAppSelector(
    (state) => businessSelectors.getBusinessForId(state, txn.businessId ?? '')?.nextInvoiceNo ?? '1000',
  );

  const salesTaxCoa = useAppSelector(
    (state) => {
      const taxAccounts = accountsSelectors.getSalesTaxAccountsByBusiness(state);
      const taxAccount = taxAccounts?.get(txn.businessId ?? '') || null;
      return chartOfAccountsTypes.mkChartOfAccount({ type: 'ACCOUNT', id: taxAccount?.id ?? '' });
    },
    (prev, next) => prev.id === next.id && prev.type === next.type,
  );

  const options: any[] = usePaymentOptions(props) ?? [];

  const estimateOptions = [];
  const isEstimate = txn.subtype === 'ESTIMATE';
  if (isEstimate) {
    estimateOptions.push({
      id: 'status-update',
      key: 'status-update',
      label: 'Update Status',
      startIcon: <EditNoteOutlinedIcon />,
      subMenu: [
        {
          id: 'status-update-pending',
          key: 'status-update-pending',
          label: '',
          startIcon: <InvoiceStatusChip value='PENDING' />,
          onSelect: () => {
            updateEstimateStatus(txn, 'PENDING');
          },
        },
        {
          id: 'status-update-accepted',
          key: 'status-update-accepted',
          label: '',
          startIcon: <InvoiceStatusChip value='ACCEPTED' />,
          onSelect: () => {
            updateEstimateStatus(txn, 'ACCEPTED');
          },
        },
        {
          id: 'status-update-declined',
          key: 'status-update-declined',
          label: '',
          startIcon: <InvoiceStatusChip value='DECLINED' />,
          onSelect: () => {
            updateEstimateStatus(txn, 'DECLINED');
          },
        },
      ],
    });
    estimateOptions.push({
      id: 'invoice-convert',
      key: 'invoice-convert',
      startIcon: <CopyAllIcon />,
      label: 'Generate Invoice',
      onSelect: () => {
        closeMenu();
        const clonedTxn = transactionsUtils.convertEstimateToInvoiceTransaction(txn, salesTaxCoa, {
          invoiceNo: `${nextInvoiceNo ?? '1000'}`,
          payee: `Invoice ${nextInvoiceNo ?? '1000'}`,
          subtype: TransactionSubType.Invoice,
          postedOn: DateTime.local().toISODate(),
          sentOn: '',
        });
        if (clonedTxn) {
          saveTransactions([clonedTxn]);
        }
      },
    });

    estimateOptions.push({
      id: 'invoice-clone',
      key: 'invoice-clone',
      startIcon: <FileCopyIcon />,
      label: 'Clone Estimate',
      onSelect: () => {
        closeMenu();
        const clonedTxn = transactionsUtils.cloneInvoiceTransaction(txn, {
          invoiceNo: `${nextInvoiceNo ?? '1000'}`,
          subtype: TransactionSubType.Estimate,
          postedOn: DateTime.local().toISODate(),
          sentOn: '',
        });
        if (clonedTxn) {
          saveTransactions([clonedTxn]);
        }
      },
    });
  }

  options.splice(1, 0, ...estimateOptions);
  if (options.length > 0 && !paymentOnly) {
    options[options.length - 1].divider = true;
  }
  return options;
};
